import { createReducer } from '../../../../helpers/reducer';
import actions from './actions';
import {
  ILegalEntityRecord,
  IStateOption,
  LegalEntityFieldErrors,
  LegalEntityFieldUpdates,
  LegalEntityFormField,
} from './types';
import { FormShape, ICreateLegalEntityState, IFormFieldState } from './reducer.types';

const emptyField: IFormFieldState = { value: '', error: '', touched: false };

const initialForm: FormShape = {
  name: { ...emptyField },
  tax_registration_number: { ...emptyField },
  street: { ...emptyField },
  city: { ...emptyField },
  state_id: { ...emptyField },
  zip: { ...emptyField },
};

const initState: ICreateLegalEntityState = {
  isSubmitting: false,
  isLoading: false,
  states: [],
  isLoadingStates: false,
  form: initialForm,
};

function onResetForm(state: ICreateLegalEntityState) {
  return { ...state, isSubmitting: false, form: initialForm };
}

function onChangeField(
  state: ICreateLegalEntityState,
  { payload }: { payload: { field: LegalEntityFormField; value: string } },
) {
  const current = state.form[payload.field];

  return {
    ...state,
    form: {
      ...state.form,
      [payload.field]: { ...current, value: payload.value, error: '', touched: true },
    },
  };
}

function onChangeFields(state: ICreateLegalEntityState, { payload }: { payload: LegalEntityFieldUpdates }) {
  const nextForm = (Object.keys(payload) as LegalEntityFormField[]).reduce(
    (acc, field) => ({
      ...acc,
      [field]: { ...state.form[field], value: payload[field] ?? '', error: '', touched: true },
    }),
    state.form,
  );

  return { ...state, form: nextForm };
}

function onTouchAll(state: ICreateLegalEntityState) {
  const form = Object.keys(state.form).reduce((acc, key) => {
    const field = key as LegalEntityFormField;
    acc[field] = { ...state.form[field], touched: true };

    return acc;
  }, {} as FormShape);

  return { ...state, form };
}

function onLoadStatesRequest(state: ICreateLegalEntityState) {
  return { ...state, isLoadingStates: true };
}

function onLoadStatesSuccess(state: ICreateLegalEntityState, { payload }: { payload: IStateOption[] }) {
  return { ...state, isLoadingStates: false, states: payload };
}

function onCreateRequest(state: ICreateLegalEntityState) {
  return { ...state, isSubmitting: true };
}

function onCreateSuccess(state: ICreateLegalEntityState) {
  return { ...state, isSubmitting: false };
}

function onCreateError(
  state: ICreateLegalEntityState,
  { payload }: { payload: { fieldErrors: LegalEntityFieldErrors } },
) {
  const form = Object.keys(state.form).reduce((acc, key) => {
    const field = key as LegalEntityFormField;
    const error = payload.fieldErrors[field] ?? '';
    acc[field] = { ...state.form[field], error, touched: true };

    return acc;
  }, {} as FormShape);

  return { ...state, isSubmitting: false, form };
}

function onLoadRequest(state: ICreateLegalEntityState) {
  return { ...state, isLoading: true, form: initialForm };
}

function onLoadSuccess(state: ICreateLegalEntityState, { payload }: { payload: ILegalEntityRecord }) {
  const filledForm: FormShape = {
    name: { value: payload.name ?? '', error: '', touched: false },
    tax_registration_number: { value: payload.tax_registration_number ?? '', error: '', touched: false },
    street: { value: payload.street ?? '', error: '', touched: false },
    city: { value: payload.city ?? '', error: '', touched: false },
    state_id: { value: payload.state_id ?? '', error: '', touched: false },
    zip: { value: payload.zip ?? '', error: '', touched: false },
  };

  return { ...state, isLoading: false, form: filledForm };
}

function onLoadError(state: ICreateLegalEntityState) {
  return { ...state, isLoading: false };
}

export default createReducer(initState, {
  [actions.RESET_FORM]: onResetForm,
  [actions.CHANGE_FIELD]: onChangeField,
  [actions.CHANGE_FIELDS]: onChangeFields,
  [actions.TOUCH_ALL]: onTouchAll,
  [actions.LOAD_STATES_REQUEST]: onLoadStatesRequest,
  [actions.LOAD_STATES_SUCCESS]: onLoadStatesSuccess,
  [actions.CREATE_REQUEST]: onCreateRequest,
  [actions.CREATE_SUCCESS]: onCreateSuccess,
  [actions.CREATE_ERROR]: onCreateError,
  [actions.LOAD_REQUEST]: onLoadRequest,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.LOAD_ERROR]: onLoadError,
  [actions.UPDATE_REQUEST]: onCreateRequest,
  [actions.UPDATE_SUCCESS]: onCreateSuccess,
  [actions.UPDATE_ERROR]: onCreateError,
});
